import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { sendSmsCode } from '../api/auth';
import GoogleSignInButton from '../components/GoogleSignInButton';
import BrandMark from '../components/BrandMark';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, loginWithGoogle, loginWithSmsCode, isLoading, error, isAuthenticated, clearError } = useAuthStore();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsChallengeId, setSmsChallengeId] = useState<string | null>(null);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsSending, setSmsSending] = useState(false);
  const [smsNotice, setSmsNotice] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  useEffect(() => {
    if (smsCountdown <= 0) return;
    const timer = window.setInterval(() => {
      setSmsCountdown((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [smsCountdown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!identifier.trim() || !password.trim()) {
      return;
    }

    const success = await login(identifier, password);
    if (success) {
      navigate('/', { replace: true });
    }
  };

  const handleGoogleCredential = async (credential: string) => {
    const success = await loginWithGoogle(credential);
    if (success) {
      navigate('/', { replace: true });
    }
  };

  const handleSendSmsCode = async () => {
    if (smsPhone.length !== 11) {
      return;
    }

    setSmsSending(true);
    setSmsNotice(null);
    clearError();

    try {
      const response = await sendSmsCode(smsPhone);
      if (response.code !== 0 || !response.data) {
        setSmsNotice(response.message || 'Unable to send verification code.');
        return;
      }
      setSmsChallengeId(response.data.challengeId);
      setSmsCountdown(response.data.retryAfter);
      setSmsNotice(`Code sent to ${response.data.phone}.`);
    } catch (requestError: any) {
      setSmsNotice(requestError.response?.data?.message || 'Unable to send verification code.');
    } finally {
      setSmsSending(false);
    }
  };

  const handleSmsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!smsChallengeId || smsCode.length !== 6) {
      return;
    }

    const success = await loginWithSmsCode(smsChallengeId, smsCode);
    if (success) {
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="dark-grid relative min-h-screen overflow-hidden text-white">
      <div className="pointer-events-none absolute left-[-12rem] top-[-12rem] h-[34rem] w-[34rem] rounded-full bg-cyan/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-[-15rem] right-[-8rem] h-[38rem] w-[38rem] rounded-full bg-amber/10 blur-[130px]" />

      <header className="relative z-10 mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-10">
        <BrandMark inverse linked={false} />
        <span className="hidden rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-white/50 sm:inline-flex">
          Pokémon cards only
        </span>
      </header>

      <main className="relative z-10 mx-auto grid min-h-[calc(100vh-5rem)] max-w-7xl items-center gap-12 px-5 pb-16 pt-8 sm:px-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(360px,.8fr)] lg:px-10 lg:pb-20 lg:pt-4">
        <section className="min-w-0 max-w-3xl">
          <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-cyan/20 bg-cyan/10 px-3.5 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-cyan">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan shadow-[0_0_10px_rgba(25,211,197,.9)]" />
            Cross-language card resolution
          </div>
          <h1 className="max-w-2xl text-5xl font-bold leading-[0.98] tracking-[-0.055em] sm:text-6xl lg:text-7xl">
            One card.
            <span className="block text-white/35">Every market.</span>
            <span className="block text-cyan">Any language.</span>
          </h1>
          <p className="mt-7 max-w-xl text-base leading-7 text-white/58 sm:text-lg">
            Identify a Pokémon card from an image, printed number, or mixed Chinese, Japanese and English text—then compare matched sold prices in USD.
          </p>

          <div className="mt-10 w-full max-w-2xl overflow-hidden rounded-[24px] border border-white/10 bg-white/[0.055] shadow-2xl backdrop-blur-md">
            <div className="flex min-w-0 items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
              <span className="eyebrow min-w-0 truncate text-white/38">Identity map preview</span>
              <span className="flex-shrink-0 font-mono text-[9px] text-cyan sm:text-[10px]">MATCHED</span>
            </div>
            <div className="grid gap-5 p-5 sm:grid-cols-[1fr_auto_1.12fr] sm:items-center sm:p-6">
              <div className="min-w-0 space-y-2">
                {['変装したピカチュウ', '变装皮卡丘', '204/XY-P'].map((query) => (
                  <div key={query} className="rounded-xl border border-white/10 bg-black/20 px-3.5 py-2.5 font-mono text-xs text-white/64">
                    {query}
                  </div>
                ))}
              </div>
              <div className="hidden h-px w-12 bg-gradient-to-r from-white/10 to-cyan sm:block" aria-hidden="true" />
              <div className="min-w-0 rounded-2xl border border-cyan/20 bg-cyan/[0.08] p-4">
                <p className="eyebrow text-cyan">Canonical identity</p>
                <p className="mt-2 text-base font-semibold text-white">Poncho-wearing Pikachu</p>
                <p className="mt-1 font-mono text-xs text-white/50">204/XY-P · Japanese promo</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {['eBay', 'Mercari', 'Kale'].map((market) => (
                    <span key={market} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-semibold text-white/52">
                      {market}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto min-w-0 w-full max-w-md rounded-[28px] border border-white/10 bg-white/[0.075] p-2 shadow-[0_30px_100px_rgba(0,0,0,.38)] backdrop-blur-xl">
          <div className="rounded-[22px] border border-white/10 bg-[#111827]/95 p-6 sm:p-8">
            <p className="eyebrow text-amber">Collector access</p>
            <h2 className="mt-3 text-2xl font-semibold tracking-[-0.035em]">Start searching globally</h2>
            <p className="mt-2 text-sm leading-6 text-white/48">Sign in is required to search images, resolve identities, and view sold-price evidence.</p>

            {error && (
              <div className="mt-5 rounded-xl border border-red-400/25 bg-red-400/10 px-4 py-3" role="alert">
                <p className="text-sm text-red-200">{error}</p>
              </div>
            )}

            <div className="mt-7 rounded-2xl bg-white p-3">
              <GoogleSignInButton onCredential={handleGoogleCredential} isLoading={isLoading} />
            </div>
            <p className="mt-3 text-center text-[11px] leading-5 text-white/35">Free to use. Your Google email creates or reuses your collector account.</p>

            <form onSubmit={handleSmsSubmit} className="mt-6 border-t border-white/10 pt-5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/42">Phone code access</p>
                {smsCountdown > 0 && (
                  <span className="font-mono text-[10px] text-cyan">{smsCountdown}s</span>
                )}
              </div>

              <div className="mt-4 flex overflow-hidden rounded-xl border border-white/10 bg-black/20 focus-within:border-cyan/50">
                <span className="flex items-center border-r border-white/10 px-3 text-sm text-white/42">+86</span>
                <input
                  id="sms-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={11}
                  value={smsPhone}
                  onChange={(e) => {
                    setSmsPhone(e.target.value.replace(/\D/g, '').slice(0, 11));
                    setSmsChallengeId(null);
                    setSmsCode('');
                    setSmsCountdown(0);
                    setSmsNotice(null);
                  }}
                  placeholder="Mainland China phone"
                  aria-label="Mainland China phone"
                  className="min-w-0 flex-1 bg-transparent px-3 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none"
                  disabled={smsSending || isLoading}
                />
              </div>

              {smsChallengeId && (
                <input
                  id="sms-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  aria-label="Verification code"
                  className="mt-3 w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-cyan/50 focus:outline-none"
                  disabled={isLoading}
                />
              )}

              {smsNotice && (
                <p className="mt-3 text-xs leading-5 text-white/45">{smsNotice}</p>
              )}

              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={handleSendSmsCode}
                  disabled={smsSending || isLoading || smsPhone.length !== 11 || smsCountdown > 0}
                  className="rounded-xl border border-cyan/25 bg-cyan/10 px-4 py-3 text-sm font-semibold text-cyan transition-colors hover:bg-cyan/15 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {smsCountdown > 0 ? 'Code sent' : smsSending ? 'Sending…' : smsChallengeId ? 'Resend code' : 'Send code'}
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !smsChallengeId || smsCode.length !== 6}
                  className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isLoading ? 'Signing in…' : 'Verify and sign in'}
                </button>
              </div>
            </form>

            <details className="group mt-6 border-t border-white/10 pt-5">
              <summary className="cursor-pointer list-none text-center text-xs font-semibold text-white/42 transition-colors hover:text-white/70">
                Existing legacy account
              </summary>
              <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div>
              <label
                htmlFor="identifier"
                    className="mb-1.5 block text-xs font-semibold text-white/60"
              >
                    Username or email
              </label>
              <input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="Legacy account identifier"
                    className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-cyan/50 focus:outline-none"
                disabled={isLoading}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                    className="mb-1.5 block text-xs font-semibold text-white/60"
              >
                    Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 pr-11 text-sm text-white placeholder:text-white/25 focus:border-cyan/50 focus:outline-none"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/35 transition-colors hover:text-white/70"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

                  <div className="flex items-center">
              <input
                id="remember"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-white/20 bg-black/20 text-cyan focus:ring-cyan"
              />
              <label
                htmlFor="remember"
                      className="ml-2 text-xs text-white/45"
              >
                      Keep me signed in
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading || !identifier.trim() || !password.trim()}
                    className="w-full rounded-xl border border-white/15 bg-white/10 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40"
            >
                    {isLoading ? 'Signing in…' : 'Sign in with legacy account'}
            </button>
          </form>
            </details>

            <div className="mt-7 flex items-center justify-center gap-5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/25">
              <span>Image search</span>
              <span className="h-1 w-1 rounded-full bg-white/20" />
              <span>USD prices</span>
              <span className="h-1 w-1 rounded-full bg-white/20" />
              <span>Global markets</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
